import { toNaturalTargetSlot, toNaturalTargetSlots, toNaturalBandDigits } from '@/adapters/primitive/_pure/model/natural/columnArithmetic';
import type { NaturalBandState } from '@/adapters/primitive/_contract/natural/machine';

export type NaturalSettlementOutcome =
    | 'ignored'
    | 'passed'
    | 'blocked'
    | 'swallowed'
    | 'added'
    | 'cancelled';

export type NaturalSettlementCell = {
    /** Local digit on the incoming band. */
    digit: number;
    targetSlot: number;
    outcome: NaturalSettlementOutcome;
};

type SettlementArgs = {
    radix: number;
    /** Receiver band in receiver slots; a deficit marks an obligation at Zero, not a counted cell. */
    receiver: NaturalBandState;
    incoming: NaturalBandState;
    /** Maps an incoming digit onto its receiver slot. */
    slotOffset: number;
};

export function classifyNaturalSettlementCells(args: SettlementArgs): NaturalSettlementCell[] {
    const { radix, receiver, incoming, slotOffset } = args;
    return Array.from({ length: radix }, (_, digit): NaturalSettlementCell => {
        const targetSlot = toNaturalTargetSlot({ digit, slotOffset, radix });
        const outcome: NaturalSettlementOutcome = incoming.overhangSlots.includes(digit)
            ? 'passed'
            : !incoming.countedSlots.includes(digit)
                ? 'ignored'
                : receiver.overhangSlots.includes(targetSlot)
                    ? 'blocked'
                    : receiver.countedSlots.includes(targetSlot)
                        ? 'cancelled'
                        : targetSlot === 0
                            ? 'swallowed'
                            : 'added';
        return { digit, targetSlot, outcome };
    });
}

export function applyNaturalSettlementCells(args: {
    receiver: NaturalBandState;
    cells: readonly NaturalSettlementCell[];
}): NaturalBandState {
    const counted = new Set(args.receiver.countedSlots);
    let marker = args.receiver.marker;
    for (const cell of args.cells) {
        if (cell.outcome === 'added') counted.add(cell.targetSlot);
        if (cell.outcome === 'swallowed' && cell.targetSlot === 0) marker = null;
        if (cell.outcome !== 'cancelled') continue;
        counted.delete(cell.targetSlot);
    }
    return {
        power: args.receiver.power,
        countedSlots: [...counted].sort((left, right) => left - right),
        overhangSlots: [...args.receiver.overhangSlots],
        marker,
    };
}

export function settleNaturalBandCells(args: SettlementArgs): NaturalBandState {
    return applyNaturalSettlementCells({
        receiver: args.receiver,
        cells: classifyNaturalSettlementCells(args),
    });
}

export function settleNaturalBandState(args: {
    radix: number;
    heldBand: NaturalBandState;
    incomingBand: NaturalBandState;
    slotOffset: number;
}): NaturalBandState {
    return settleNaturalBandCells({
        radix: args.radix,
        receiver: args.heldBand,
        incoming: args.incomingBand,
        slotOffset: args.slotOffset,
    });
}

/** Re-expresses a Contribution-traveller band in the receiver slots it currently covers. */
export function toReceiverFrameBand(args: {
    band: NaturalBandState;
    slotOffset: number;
    radix: number;
}): NaturalBandState {
    return {
        ...args.band,
        countedSlots: toNaturalTargetSlots({ digits: args.band.countedSlots, slotOffset: args.slotOffset, radix: args.radix }),
        overhangSlots: toNaturalTargetSlots({ digits: args.band.overhangSlots, slotOffset: args.slotOffset, radix: args.radix }),
    };
}

export function toTravellerFrameBand(args: {
    band: NaturalBandState;
    slotOffset: number;
    radix: number;
}): NaturalBandState {
    return {
        ...args.band,
        countedSlots: toNaturalBandDigits({ slots: args.band.countedSlots, slotOffset: args.slotOffset, radix: args.radix }),
        overhangSlots: toNaturalBandDigits({ slots: args.band.overhangSlots, slotOffset: args.slotOffset, radix: args.radix }),
    };
}
